// @ts-check
// Intérprete de pseudocódigo estilo PSeInt (DEC-008 → PSeInt), 100% en el
// cliente (coherente con Python/Pyodide, DEC-033: el servidor no ejecuta
// pseudocódigo). Lo usa el botón "Ejecutar" del editor para correr el
// algoritmo y mostrar su salida en una consola ANTES de corregir; NO participa
// en la corrección (que sigue comparando la salida que escribe el alumno).
//
// Alcance (elección del usuario: "completo"): estructura de programa
// (Algoritmo/Proceso), Definir/tipos, asignación `<-`, Escribir/Leer, Si,
// Mientras, Para, Repetir-Hasta Que, Segun, Dimension/arreglos, SubProceso/
// Funcion, expresiones con precedencia y un núcleo de funciones internas.
// Palabras clave insensibles a mayúsculas/acentos, como PSeInt.
//
// API pública: `ejecutarPseudocodigo(codigo, { entradas })` -> resultado con
// la salida por líneas y, si procede, un error con su línea. Puro y
// determinista (las entradas se pasan por parámetro, no hay prompts): fácil de
// testear y sin diálogos del navegador.

/**
 * `salidaSinPrompts` es la salida producida tras el último `Leer`: lo escrito
 * antes de pedir un dato es un mensaje de petición, no un resultado. Se ofrece
 * como DATO ADICIONAL; `salida` sigue siendo la salida literal e íntegra.
 * @typedef {{ salida: string[], salidaSinPrompts: string[],
 *   error: { mensaje: string, linea: number|null } | null,
 *   entradasConsumidas: number, faltoEntrada: boolean }} ResultadoEjecucion
 */

/** Nodo del AST (forma dinámica según el tipo); se trata como `any`.
 * @typedef {any} Nodo */
/** Ámbito de variables (nombre canónico -> valor).
 * @typedef {Map<string, any>} Ambito */

/** Error de tiempo de análisis o ejecución con la línea de origen (1-based). */
class ErrorPseint extends Error {
  /** @param {string} mensaje @param {number|null} [linea] */
  constructor(mensaje, linea = null) {
    super(mensaje);
    this.name = 'ErrorPseint';
    /** @type {number|null} */
    this.linea = linea;
  }
}

/** Límite de iteraciones total para cortar bucles infinitos del alumno. */
const LIMITE_PASOS = 2_000_000;

/** Tipos de `Definir` cuyo `Leer` guarda el texto sin convertirlo (DEC-132). */
const TIPOS_TEXTO = new Set(['cadena', 'caracter', 'texto']);

/** @typedef {{ nombre: string, cierre: string, claves: readonly string[] }} Bloque */

/**
 * Cada bloque con su cierre tal como lo escribe la guía del alumno
 * (`frontend/guia-pseudocodigo.html`), y las palabras que lo cierran (en clave
 * canónica). Es la fuente de los mensajes que orientan: «Falta FinSi para
 * cerrar el Si de la línea 3» y «"Fin Si" se escribe junto: FinSi».
 * @type {ReadonlyMap<string, Bloque>}
 */
const BLOQUES = new Map([
  [
    'algoritmo',
    { nombre: 'el Algoritmo', cierre: 'FinAlgoritmo', claves: ['finalgoritmo', 'finproceso'] },
  ],
  [
    'proceso',
    { nombre: 'el Proceso', cierre: 'FinProceso', claves: ['finalgoritmo', 'finproceso'] },
  ],
  [
    'subproceso',
    { nombre: 'el SubProceso', cierre: 'FinSubProceso', claves: ['finsubproceso', 'finfuncion'] },
  ],
  [
    'funcion',
    { nombre: 'la Funcion', cierre: 'FinFuncion', claves: ['finsubproceso', 'finfuncion'] },
  ],
  ['si', { nombre: 'el Si', cierre: 'FinSi', claves: ['finsi'] }],
  ['mientras', { nombre: 'el Mientras', cierre: 'FinMientras', claves: ['finmientras'] }],
  ['para', { nombre: 'el Para', cierre: 'FinPara', claves: ['finpara'] }],
  ['segun', { nombre: 'el Segun', cierre: 'FinSegun', claves: ['finsegun'] }],
  ['repetir', { nombre: 'el Repetir', cierre: 'Hasta Que', claves: ['hasta'] }],
]);

/** Palabras que empiezan un programa o subprograma: dentro de un bloque solo
 * aparecen si a ese bloque le falta su cierre. */
const INICIOS_DE_PROGRAMA = new Set([
  'algoritmo',
  'proceso',
  'subproceso',
  'subalgoritmo',
  'funcion',
]);

/**
 * Palabras reservadas: no pueden ser el nombre de un SubProceso, Funcion,
 * parámetro, variable ni arreglo (13 sep 2026). Como nombre funcionaban en
 * unos sitios y en otros no, a veces sin avisar: `SubProceso mostrar(x)` se
 * llamaba con `mostrar(605)`, que es `Escribir (605)`, y el programa escribía
 * el número sin ejecutar el SubProceso.
 *
 * Son las que empiezan o cierran una instrucción o un bloque, y las que
 * cambian lo que se lee en medio de una: `Verdadero`, `Falso`, `No`, `Sin`
 * (`Escribir Sin Saltar`) y `Por` (`Por Referencia`). NO lo son las que solo
 * cuentan en medio de una instrucción y como nombre funcionan en cualquier
 * sitio (`como`, `entonces`, `hacer`, `paso`, `valor`, `y`, `fin`, `div`...):
 * `paso`, `valor` o `y` son nombres habituales. `pseint-nombres-reservados.spec.js`
 * lee del analizador las palabras que reconoce y comprueba las dos cosas.
 */
const PALABRAS_RESERVADAS = new Set([
  ...INICIOS_DE_PROGRAMA,
  ...[...BLOQUES.values()].flatMap((b) => b.claves),
  'definir',
  'dimension',
  'dimensionar',
  'escribir',
  'mostrar',
  'imprimir',
  'leer',
  'si',
  'sino',
  'mientras',
  'para',
  'repetir',
  'segun',
  'de',
  'deotromodo',
  'verdadero',
  'falso',
  'no',
  'sin',
  'por',
]);

/** Reservadas que hacen lo mismo que otra instrucción: el mensaje lo dice. */
const HACE_LO_MISMO_QUE = new Map([
  ['mostrar', 'Escribir'],
  ['imprimir', 'Escribir'],
  ['dimensionar', 'Dimension'],
  ['subalgoritmo', 'SubProceso'],
]);

// ---------------------------------------------------------------------------
// Normalización de palabras clave (insensible a mayúsculas y acentos).
// ---------------------------------------------------------------------------
/** @param {string} s @returns {string} */
function sinAcentos(s) {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}
/** Clave canónica de un identificador/palabra: minúsculas sin acentos.
 * @param {string} texto @returns {string} */
function clave(texto) {
  return sinAcentos(String(texto)).toLowerCase();
}

/**
 * Error si `token` es una palabra reservada puesta como nombre. `papel` es lo
 * que nombraba («ponle otro nombre a tu variable»). Repite la palabra como la
 * escribió el alumno, igual que el resto de mensajes.
 * @param {Token} token @param {string} papel
 */
function rechazarNombreReservado(token, papel) {
  if (token.tipo !== 'ID') return;
  const k = clave(token.valor);
  if (!PALABRAS_RESERVADAS.has(k)) return;
  const igual = HACE_LO_MISMO_QUE.get(k);
  const hace = igual ? ` (hace lo mismo que ${igual})` : '';
  throw new ErrorPseint(
    `"${token.valor}" es una palabra reservada${hace}: ponle otro nombre a tu ${papel}.`,
    token.linea,
  );
}

/**
 * Lo mismo para el nombre de un SubProceso o Funcion, que además no puede ser
 * el de una función interna: al llamarlo ganaba la interna y el subprograma
 * del alumno no se ejecutaba nunca.
 * @param {Token} token @param {string} papel "SubProceso" o "Funcion".
 */
function rechazarNombreDeSubprograma(token, papel) {
  rechazarNombreReservado(token, papel);
  if (token.tipo === 'ID' && funcionInterna(token.valor)) {
    throw new ErrorPseint(
      `"${token.valor}" ya es una función de PSeInt: ponle otro nombre a tu ${papel}.`,
      token.linea,
    );
  }
}

// ---------------------------------------------------------------------------
// Lexer
// ---------------------------------------------------------------------------
/** @typedef {{ tipo: string, valor: any, linea: number }} Token */

const OPERADORES_DOS = ['<-', '<=', '>=', '<>', '==', '&&', '||'];
const OPERADORES_UNO = ['+', '-', '*', '/', '^', '%', '=', '<', '>', '(', ')', '[', ']', ',', '&', '|', '~', ':'];

/**
 * @param {string} codigo
 * @returns {Token[]}
 */
function tokenizar(codigo) {
  /** @type {Token[]} */
  const tokens = [];
  let i = 0;
  let linea = 1;
  const n = codigo.length;
  while (i < n) {
    const c = codigo[i];
    if (c === '\n') {
      tokens.push({ tipo: 'NL', valor: '\n', linea });
      linea += 1;
      i += 1;
      continue;
    }
    if (c === ' ' || c === '\t' || c === '\r') {
      i += 1;
      continue;
    }
    // Comentarios: `//...` hasta fin de línea (PSeInt también usa `//`).
    if (c === '/' && codigo[i + 1] === '/') {
      while (i < n && codigo[i] !== '\n') i += 1;
      continue;
    }
    // Cadenas: '...' o "..."
    if (c === '"' || c === "'") {
      const comilla = c;
      let valor = '';
      i += 1;
      while (i < n && codigo[i] !== comilla) {
        if (codigo[i] === '\n') throw new ErrorPseint('Cadena de texto sin cerrar.', linea);
        valor += codigo[i];
        i += 1;
      }
      if (i >= n) throw new ErrorPseint('Cadena de texto sin cerrar.', linea);
      i += 1; // cierra comilla
      tokens.push({ tipo: 'CADENA', valor, linea });
      continue;
    }
    // Números (enteros o reales).
    if (c >= '0' && c <= '9') {
      let num = '';
      while (i < n && codigo[i] >= '0' && codigo[i] <= '9') {
        num += codigo[i];
        i += 1;
      }
      if (codigo[i] === '.' && codigo[i + 1] >= '0' && codigo[i + 1] <= '9') {
        num += '.';
        i += 1;
        while (i < n && codigo[i] >= '0' && codigo[i] <= '9') {
          num += codigo[i];
          i += 1;
        }
      }
      tokens.push({ tipo: 'NUMERO', valor: Number(num), linea });
      continue;
    }
    // Identificadores / palabras clave.
    if (/[A-Za-z_À-ſ]/.test(c)) {
      let id = '';
      while (i < n && /[A-Za-z0-9_À-ſ]/.test(codigo[i])) {
        id += codigo[i];
        i += 1;
      }
      tokens.push({ tipo: 'ID', valor: id, linea });
      continue;
    }
    // `;` al final de una instrucción: PSeInt lo admite (es opcional). Separa
    // instrucciones igual que un salto de línea (dentro de un texto no llega
    // aquí: las cadenas se leen antes).
    if (c === ';') {
      tokens.push({ tipo: 'NL', valor: ';', linea });
      i += 1;
      continue;
    }
    // `:=` asigna en otros pseudocódigos. Aquí no existe: se dice cuál es la
    // forma buena en vez de «Instrucción no válida».
    if (c === ':' && codigo[i + 1] === '=') {
      throw new ErrorPseint('":=" no existe en este pseudocódigo; para asignar usa <-.', linea);
    }
    // Operadores de dos caracteres primero.
    const dos = codigo.substr(i, 2);
    if (OPERADORES_DOS.includes(dos)) {
      tokens.push({ tipo: 'OP', valor: dos, linea });
      i += 2;
      continue;
    }
    if (OPERADORES_UNO.includes(c)) {
      tokens.push({ tipo: 'OP', valor: c, linea });
      i += 1;
      continue;
    }
    throw new ErrorPseint(`Símbolo no reconocido: "${c}".`, linea);
  }
  tokens.push({ tipo: 'EOF', valor: null, linea });
  return tokens;
}

// ---------------------------------------------------------------------------
// Parser (descenso recursivo). Produce un AST de sentencias y expresiones.
// Los saltos de línea separan sentencias; se ignoran los NL redundantes.
// ---------------------------------------------------------------------------

class Parser {
  /** @param {Token[]} tokens */
  constructor(tokens) {
    this.tokens = tokens;
    this.pos = 0;
    // Bloques abiertos, del más externo al más interno, con la línea donde se
    // abrieron: así el error de un cierre que falta dice cuál y de qué línea.
    /** @type {(Bloque & { linea: number })[]} */
    this.abiertos = [];
  }

  /** @returns {Token} */
  get actual() {
    return this.tokens[this.pos];
  }

  /** @returns {Token} */
  avanzar() {
    return this.tokens[this.pos++];
  }

  /** Salta saltos de línea. */
  saltarNL() {
    while (this.actual.tipo === 'NL') this.pos += 1;
  }

  /** ¿El token actual es la palabra clave `kw` (insensible)?
   * @param {string} kw @returns {boolean} */
  esPalabra(kw) {
    return this.actual.tipo === 'ID' && clave(this.actual.valor) === kw;
  }

  /** Consume una palabra clave obligatoria.
   * @param {string} kw @param {string} [contexto] @returns {Token} */
  esperarPalabra(kw, contexto) {
    if (!this.esPalabra(kw)) {
      throw new ErrorPseint(`Se esperaba "${kw}"${contexto ? ` ${contexto}` : ''}.`, this.actual.linea);
    }
    return this.avanzar();
  }

  /** @param {string} v @returns {boolean} */
  esOp(v) {
    return this.actual.tipo === 'OP' && this.actual.valor === v;
  }

  /** @param {string} v @returns {Token} */
  esperarOp(v) {
    if (!this.esOp(v)) throw new ErrorPseint(`Se esperaba "${v}".`, this.actual.linea);
    return this.avanzar();
  }

  /**
   * Programa: [Definir/subprocesos...] Algoritmo/Proceso <nombre> <cuerpo>
   * Fin... , con SubProceso/Funcion antes o después. Se recogen todos los
   * subprogramas y el cuerpo principal.
   * @returns {{ principal: any[], subprogramas: Map<string, any> }}
   */
  parsearPrograma() {
    /** @type {Map<string, any>} */
    const subprogramas = new Map();
    /** @type {any[]|null} */
    let principal = null;
    this.rechazarSubprogramasConNombreReservado();
    this.saltarNL();
    while (this.actual.tipo !== 'EOF') {
      this.saltarNL();
      if (this.actual.tipo === 'EOF') break;
      if (this.esPalabra('algoritmo') || this.esPalabra('proceso')) {
        const inicio = this.avanzar();
        // nombre opcional del algoritmo
        if (this.actual.tipo === 'ID') this.avanzar();
        this.abrir(clave(inicio.valor), inicio.linea);
        principal = this.parsearSentenciasHasta();
        this.avanzarCierre();
      } else if (this.esPalabra('subproceso') || this.esPalabra('subalgoritmo') || this.esPalabra('funcion')) {
        const sp = this.parsearSubprograma();
        subprogramas.set(clave(sp.nombre), sp);
      } else {
        throw new ErrorPseint(
          'Fuera de "Algoritmo"/"Proceso" solo se permiten definiciones de SubProceso o Funcion.',
          this.actual.linea
        );
      }
      this.saltarNL();
    }
    if (principal === null) {
      throw new ErrorPseint('Falta el bloque principal: escribe "Algoritmo ... FinAlgoritmo".', 1);
    }
    return { principal, subprogramas };
  }

  /**
   * El nombre de cada SubProceso y Funcion, antes de analizar nada más. Uno
   * escrito DESPUÉS del Algoritmo se llama antes de que se lea su cabecera:
   * `mostrar("Total", 5)` se leía como `Escribir ("Total", 5)` y el alumno
   * recibía «Se esperaba ")"» en la llamada en vez de oír que el nombre no vale.
   */
  rechazarSubprogramasConNombreReservado() {
    const t = this.tokens;
    t.forEach((inicio, i) => {
      if (inicio.tipo !== 'ID' || (i > 0 && t[i - 1].tipo !== 'NL')) return;
      const k = clave(inicio.valor);
      if (k !== 'subproceso' && k !== 'subalgoritmo' && k !== 'funcion') return;
      // La cabecera que lee `parsearSubprograma`: [retorno <-] nombre.
      let j = i + 1;
      if (t[j].tipo === 'ID' && t[j + 1].tipo === 'OP' && t[j + 1].valor === '<-') j += 2;
      rechazarNombreDeSubprograma(t[j], k === 'funcion' ? 'Funcion' : 'SubProceso');
    });
  }

  /** SubProceso [retorno <-] nombre(param, ...) cuerpo FinSubProceso/FinFuncion */
  parsearSubprograma() {
    const inicio = this.avanzar(); // subproceso/funcion
    let retorno = null;
    // Forma "SubProceso r <- nombre(...)" (retorno explícito).
    if (this.tokens[this.pos].tipo === 'ID' && this.tokens[this.pos + 1] &&
        this.tokens[this.pos + 1].tipo === 'OP' && this.tokens[this.pos + 1].valor === '<-') {
      rechazarNombreReservado(this.actual, 'variable');
      retorno = this.avanzar().valor; // variable de retorno
      this.avanzar(); // <-
    }
    if (this.actual.tipo !== 'ID') throw new ErrorPseint('Falta el nombre del subproceso/función.', this.actual.linea);
    rechazarNombreDeSubprograma(
      this.actual,
      clave(inicio.valor) === 'funcion' ? 'Funcion' : 'SubProceso',
    );
    const nombre = this.avanzar().valor;
    /** @type {{ nombre: string, porReferencia: boolean }[]} */
    const params = [];
    if (this.esOp('(')) {
      this.avanzar();
      while (!this.esOp(')')) {
        // `p(Por)` o `p(Por, x)`: ese Por no es el modo, es el nombre.
        const tras = this.tokens[this.pos + 1];
        if (this.esPalabra('por') && tras.tipo === 'OP' && [',', ')'].includes(tras.valor)) {
          rechazarNombreReservado(this.actual, 'parámetro');
        }
        // Modificadores PSeInt "Por Valor"/"Por Referencia" opcionales.
        let porReferencia = false;
        if (this.esPalabra('por')) {
          this.avanzar();
          if (this.esPalabra('referencia')) porReferencia = true;
          this.avanzar();
        }
        if (this.actual.tipo !== 'ID') throw new ErrorPseint('Parámetro inválido.', this.actual.linea);
        rechazarNombreReservado(this.actual, 'parámetro');
        const parametro = this.avanzar().valor;
        // `x Por Referencia` es como se escribe en otros pseudocódigos; la guía
        // (apartado 19) lo enseña delante: `Por Referencia x`.
        if (this.esPalabra('por')) {
          const modo =
            clave(this.tokens[this.pos + 1].valor ?? '') === 'valor'
              ? 'Por Valor'
              : 'Por Referencia';
          throw new ErrorPseint(
            `"${modo}" va delante del nombre del parámetro: ${nombre}(${modo} ${parametro}).`,
            this.actual.linea,
          );
        }
        params.push({ nombre: parametro, porReferencia });
        if (this.esOp(',')) this.avanzar();
        else break;
      }
      this.esperarOp(')');
    }
    this.abrir(clave(inicio.valor) === 'funcion' ? 'funcion' : 'subproceso', inicio.linea);
    const cuerpo = this.parsearSentenciasHasta();
    this.avanzarCierre();
    return { nombre, retorno, params, cuerpo };
  }

  /**
   * Apunta como abierto un bloque (clave de `BLOQUES`) que empieza en `linea`.
   * @param {string} tipo @param {number} linea
   */
  abrir(tipo, linea) {
    this.abiertos.push({ .../** @type {Bloque} */ (BLOQUES.get(tipo)), linea });
  }

  /** Consume el cierre del bloque abierto más interno, o dice cuál falta. */
  avanzarCierre() {
    const bloque = this.abiertos[this.abiertos.length - 1];
    this.saltarNL();
    if (this.actual.tipo === 'ID' && bloque.claves.includes(clave(this.actual.valor))) {
      this.abiertos.pop();
      this.avanzar();
      return;
    }
    throw new ErrorPseint(
      `Falta ${bloque.cierre} para cerrar ${bloque.nombre} de la línea ${bloque.linea}.`,
      this.actual.linea,
    );
  }

  /**
   * ¿El token actual termina un bloque abierto? Lo termina su cierre, el de
   * un bloque que lo contiene (entonces al de dentro le falta el suyo) o el
   * comienzo de otro programa o subprograma (`SubProceso p(...)`).
   * @returns {boolean}
   */
  cierraUnBloqueAbierto() {
    if (this.actual.tipo !== 'ID') return false;
    const k = clave(this.actual.valor);
    if (this.abiertos.some((b) => b.claves.includes(k))) return true;
    return INICIOS_DE_PROGRAMA.has(k) && this.tokens[this.pos + 1].tipo === 'ID';
  }

  /**
   * `Fin Si`, con espacio, es como se cierra en otros pseudocódigos. Sigue
   * siendo un error, pero el mensaje dice cómo se escribe aquí: `FinSi`.
   */
  rechazarCierreSeparado() {
    const fin = this.actual;
    const siguiente = this.tokens[this.pos + 1];
    if (siguiente.tipo !== 'ID' || siguiente.linea !== fin.linea) return;
    const bloque = BLOQUES.get(clave(siguiente.valor));
    if (bloque && bloque.cierre.startsWith('Fin')) {
      throw new ErrorPseint(
        `"${fin.valor} ${siguiente.valor}" se escribe junto: ${bloque.cierre}.`,
        fin.linea,
      );
    }
  }

  /**
   * Sentencias hasta el cierre de un bloque abierto (que NO consume) o hasta
   * una de las palabras de `extra` (el `Sino` del Si).
   * @param {string[]} [extra]
   * @returns {Nodo[]}
   */
  parsearSentenciasHasta(extra = []) {
    /** @type {Nodo[]} */
    const sentencias = [];
    this.saltarNL();
    while (this.actual.tipo !== 'EOF') {
      this.rechazarAsignacionAReservada();
      if (this.actual.tipo === 'ID' && extra.includes(clave(this.actual.valor))) break;
      if (this.cierraUnBloqueAbierto()) break;
      const s = this.parsearSentencia();
      if (s) sentencias.push(s);
      this.saltarNL();
    }
    return sentencias;
  }

  /**
   * `Si <- 3`: una palabra reservada al principio de una instrucción y
   * seguida de `<-`. Se mira antes que nada, porque si no se lee como lo que
   * es (`FinSi` cerraría el Si, `Hasta` el Repetir) y el error que sale luego
   * no dice qué pasa.
   */
  rechazarAsignacionAReservada() {
    const siguiente = this.tokens[this.pos + 1];
    if (siguiente && siguiente.tipo === 'OP' && siguiente.valor === '<-') {
      rechazarNombreReservado(this.actual, 'variable');
    }
  }

  /** @returns {Nodo} */
  parsearSentencia() {
    const t = this.actual;
    if (t.tipo !== 'ID') {
      throw new ErrorPseint('Instrucción no válida.', t.linea);
    }
    const kw = clave(t.valor);
    if (kw === 'fin') this.rechazarCierreSeparado();
    switch (kw) {
      case 'definir':
        return this.parsearDefinir();
      case 'dimension':
      case 'dimensionar':
        return this.parsearDimension();
      case 'escribir':
      case 'mostrar':
      case 'imprimir':
        return this.parsearEscribir();
      case 'leer':
        return this.parsearLeer();
      case 'si':
        return this.parsearSi();
      case 'mientras':
        return this.parsearMientras();
      case 'para':
        return this.parsearPara();
      case 'repetir':
        return this.parsearRepetir();
      case 'segun':
        return this.parsearSegun();
      default:
        // Asignación (id[...] <- expr / id = expr) o llamada a subproceso.
        return this.parsearAsignacionOLlamada();
    }
  }

  parsearDefinir() {
    const linea = this.avanzar().linea; // Definir
    const nombres = [];
    for (;;) {
      if (this.actual.tipo !== 'ID') throw new ErrorPseint('Nombre de variable inválido en Definir.', this.actual.linea);
      rechazarNombreReservado(this.actual, 'variable');
      nombres.push(this.avanzar().valor);
      if (!this.esOp(',')) break;
      this.avanzar();
    }
    this.esperarPalabra('como', 'en Definir');
    // El tipo declarado ("Entero", "Caracter", ...) lo IGNORA el intérprete,
    // que es dinámico, pero se CONSERVA en el nodo desde DEC-107: el traductor
    // a IR lo necesita, porque `Declaracion` sí exige tipo y adivinarlo
    // produciría declaraciones con el tipo equivocado.
    let tipoDeclarado = null;
    if (this.actual.tipo === 'ID') tipoDeclarado = String(this.avanzar().valor);
    // Permite "Definir v Como Entero" y arreglos "Definir v[10] Como ..." poco comunes; se ignora tamaño aquí.
    return { tipo: 'definir', nombres, tipoDeclarado, linea };
  }

  parsearDimension() {
    const linea = this.avanzar().linea; // Dimension
    /** @type {{ nombre: string, dimensiones: any[] }[]} */
    const arreglos = [];
    for (;;) {
      if (this.actual.tipo !== 'ID') throw new ErrorPseint('Nombre de arreglo inválido en Dimension.', this.actual.linea);
      rechazarNombreReservado(this.actual, 'arreglo');
      const nombre = this.avanzar().valor;
      const dimensiones = [];
      this.esperarOp('[');
      for (;;) {
        dimensiones.push(this.parsearExpresion());
        if (!this.esOp(',')) break;
        this.avanzar();
      }
      this.esperarOp(']');
      arreglos.push({ nombre, dimensiones });
      if (!this.esOp(',')) break;
      this.avanzar();
    }
    return { tipo: 'dimension', arreglos, linea };
  }

  parsearEscribir() {
    const linea = this.avanzar().linea; // Escribir/Mostrar
    let sinSalto = false;
    if (this.esPalabra('sin')) {
      this.avanzar();
      // PSeInt admite «Sin Saltar» y «Sin Bajar».
      if (this.esPalabra('bajar')) this.avanzar();
      else this.esperarPalabra('saltar', 'tras "Sin"');
      sinSalto = true;
    }
    const partes = [this.parsearExpresion()];
    while (this.esOp(',')) {
      this.avanzar();
      partes.push(this.parsearExpresion());
    }
    return { tipo: 'escribir', partes, sinSalto, linea };
  }

  parsearLeer() {
    const linea = this.avanzar().linea; // Leer
    rechazarNombreReservado(this.actual, 'variable');
    const destinos = [this.parsearLValue()];
    while (this.esOp(',')) {
      this.avanzar();
      rechazarNombreReservado(this.actual, 'variable');
      destinos.push(this.parsearLValue());
    }
    return { tipo: 'leer', destinos, linea };
  }

  parsearSi() {
    const linea = this.avanzar().linea; // Si
    const condicion = this.parsearExpresion();
    this.esperarPalabra('entonces', 'tras la condición del Si');
    this.abrir('si', linea);
    const entonces = this.parsearSentenciasHasta(['sino']);
    /** @type {Nodo[]} */
    let sino = [];
    if (this.esPalabra('sino')) {
      this.avanzar();
      sino = this.parsearSentenciasHasta();
    }
    this.avanzarCierre();
    return { tipo: 'si', condicion, entonces, sino, linea };
  }

  parsearMientras() {
    const linea = this.avanzar().linea; // Mientras
    const condicion = this.parsearExpresion();
    if (this.esPalabra('hacer')) this.avanzar();
    this.abrir('mientras', linea);
    const cuerpo = this.parsearSentenciasHasta();
    this.avanzarCierre();
    return { tipo: 'mientras', condicion, cuerpo, linea };
  }

  parsearPara() {
    const linea = this.avanzar().linea; // Para
    rechazarNombreReservado(this.actual, 'variable');
    const variable = this.avanzar().valor; // id
    if (!this.esOp('<-') && !this.esOp('=')) throw new ErrorPseint('Falta "<-" en el Para.', this.actual.linea);
    this.avanzar();
    const desde = this.parsearExpresion();
    this.esperarPalabra('hasta', 'en el Para');
    const hasta = this.parsearExpresion();
    let paso = null;
    if (this.esPalabra('con')) {
      this.avanzar();
      this.esperarPalabra('paso', 'tras "Con"');
      paso = this.parsearExpresion();
    }
    if (this.esPalabra('hacer')) this.avanzar();
    this.abrir('para', linea);
    const cuerpo = this.parsearSentenciasHasta();
    this.avanzarCierre();
    return { tipo: 'para', variable, desde, hasta, paso, cuerpo, linea };
  }

  parsearRepetir() {
    const linea = this.avanzar().linea; // Repetir
    this.abrir('repetir', linea);
    const cuerpo = this.parsearSentenciasHasta();
    this.avanzarCierre(); // Hasta
    if (this.esPalabra('que')) this.avanzar();
    const condicion = this.parsearExpresion();
    return { tipo: 'repetir', cuerpo, condicion, linea };
  }

  parsearSegun() {
    const linea = this.avanzar().linea; // Segun
    const expr = this.parsearExpresion();
    if (this.esPalabra('hacer')) this.avanzar();
    this.abrir('segun', linea);
    this.saltarNL();
    const casos = [];
    let deOtroModo = null;
    while (!this.esPalabra('finsegun')) {
      this.saltarNL();
      if (this.esPalabra('finsegun')) break;
      if (this.esPalabra('fin')) this.rechazarCierreSeparado();
      if (this.actual.tipo === 'EOF' || this.cierraUnBloqueAbierto()) break;
      if (this.esPalabra('deotromodo') || (this.esPalabra('de') )) {
        // "De Otro Modo"
        this.avanzar();
        if (this.esPalabra('otro')) this.avanzar();
        if (this.esPalabra('modo')) this.avanzar();
        this.esperarOp(':');
        deOtroModo = this.parsearSentenciasHasta();
        break;
      }
      const valores = [this.parsearExpresion()];
      while (this.esOp(',')) {
        this.avanzar();
        valores.push(this.parsearExpresion());
      }
      this.esperarOp(':');
      const cuerpo = this.parsearSentenciasCaso();
      casos.push({ valores, cuerpo });
      this.saltarNL();
    }
    this.avanzarCierre();
    return { tipo: 'segun', expr, casos, deOtroModo, linea };
  }

  /** Sentencias de un caso de Segun: hasta el siguiente caso, "De Otro Modo" o FinSegun. */
  parsearSentenciasCaso() {
    const sentencias = [];
    this.saltarNL();
    while (this.actual.tipo !== 'EOF') {
      this.rechazarAsignacionAReservada();
      if (this.esPalabra('finsegun') || this.esPalabra('deotromodo') || this.esPalabra('de')) break;
      if (this.cierraUnBloqueAbierto()) break;
      // ¿Empieza un nuevo caso "valor:"? Heurística: expresión seguida de ':'.
      const guardado = this.pos;
      if (this.esInicioDeCasoSegun()) {
        this.pos = guardado;
        break;
      }
      this.pos = guardado;
      sentencias.push(this.parsearSentencia());
      this.saltarNL();
    }
    return sentencias;
  }

  /** Detecta "expr[,expr]* :" al inicio (un nuevo caso), sin consumir. */
  esInicioDeCasoSegun() {
    try {
      this.parsearExpresion();
      while (this.esOp(',')) {
        this.avanzar();
        this.parsearExpresion();
      }
      return this.esOp(':');
    } catch {
      return false;
    }
  }

  parsearAsignacionOLlamada() {
    const inicio = this.pos;
    const lvalue = this.parsearLValue();
    if (this.esOp('<-') || this.esOp('=') || this.esOp('==')) {
      const linea = this.avanzar().linea;
      const valor = this.parsearExpresion();
      return { tipo: 'asignacion', destino: lvalue, valor, linea };
    }
    // Si el lvalue era una llamada `nombre(args)`, es una llamada-sentencia.
    if (lvalue.tipo === 'llamada') {
      return { tipo: 'llamadaSentencia', llamada: lvalue, linea: lvalue.linea };
    }
    this.pos = inicio;
    throw new ErrorPseint('Instrucción no válida: se esperaba una asignación (<-) o una llamada.', this.actual.linea);
  }

  /** Objetivo asignable: variable, elemento de arreglo, o (para llamadas) llamada.
   * @returns {Nodo} */
  parsearLValue() {
    if (this.actual.tipo !== 'ID') throw new ErrorPseint('Se esperaba una variable.', this.actual.linea);
    const linea = this.actual.linea;
    const nombre = this.avanzar().valor;
    if (this.esOp('(')) {
      const args = this.parsearArgumentos(nombre);
      // Una función interna con otro número de datos es un error aunque esa
      // línea no llegue a ejecutarse. Hasta el 13 sep 2026 los de más se
      // ignoraban sin avisar: `redon(x, 1)` redondeaba al entero.
      const interna = funcionInterna(nombre);
      if (interna && args.length !== interna.datos) {
        const datos = `${interna.datos} ${interna.datos === 1 ? 'dato' : 'datos'}`;
        throw new ErrorPseint(`"${nombre}" admite ${datos} y le has dado ${args.length}.`, linea);
      }
      return { tipo: 'llamada', nombre, args, linea };
    }
    if (this.esOp('[')) {
      const indices = [];
      this.avanzar();
      for (;;) {
        indices.push(this.parsearExpresion());
        if (!this.esOp(',')) break;
        this.avanzar();
      }
      this.esperarOp(']');
      return { tipo: 'indice', nombre, indices, linea };
    }
    return { tipo: 'variable', nombre, linea };
  }

  /** @param {string} nombre De la función o subproceso llamado. @returns {Nodo[]} */
  parsearArgumentos(nombre) {
    this.esperarOp('(');
    /** @type {Nodo[]} */
    const args = [];
    if (!this.esOp(')')) {
      for (;;) {
        // `p(Por Referencia y)`: el modo se escribe en la cabecera del
        // SubProceso (guía, apartado 19), no al llamarlo.
        const modo = this.tokens[this.pos + 1];
        if (
          this.esPalabra('por') &&
          modo.tipo === 'ID' &&
          ['referencia', 'valor'].includes(clave(modo.valor))
        ) {
          const escrito = clave(modo.valor) === 'valor' ? 'Por Valor' : 'Por Referencia';
          throw new ErrorPseint(
            `"${escrito}" va en la cabecera del SubProceso, no al llamarlo: SubProceso ${nombre}(${escrito} x).`,
            this.actual.linea,
          );
        }
        args.push(this.parsearExpresion());
        if (!this.esOp(',')) break;
        this.avanzar();
      }
    }
    this.esperarOp(')');
    return args;
  }

  // ---- Expresiones (precedencia: O < Y < NO < relacional < aditivo < mult < potencia < unario < primario) ----
  /** @returns {Nodo} */
  parsearExpresion() {
    return this.parsearO();
  }

  /** @returns {Nodo} */
  parsearO() {
    /** @type {Nodo} */
    let izq = this.parsearY();
    while (this.esPalabra('o') || this.esOp('|') || this.esOp('||')) {
      const linea = this.avanzar().linea;
      izq = { tipo: 'binaria', op: 'o', izq, der: this.parsearY(), linea };
    }
    return izq;
  }

  /** @returns {Nodo} */
  parsearY() {
    /** @type {Nodo} */
    let izq = this.parsearNo();
    while (this.esPalabra('y') || this.esOp('&') || this.esOp('&&')) {
      const linea = this.avanzar().linea;
      izq = { tipo: 'binaria', op: 'y', izq, der: this.parsearNo(), linea };
    }
    return izq;
  }

  /** @returns {Nodo} */
  parsearNo() {
    if (this.esPalabra('no') || this.esOp('~')) {
      const linea = this.avanzar().linea;
      return { tipo: 'unaria', op: 'no', valor: this.parsearNo(), linea };
    }
    return this.parsearRelacional();
  }

  /** @returns {Nodo} */
  parsearRelacional() {
    /** @type {Nodo} */
    let izq = this.parsearAditivo();
    while (
      this.esOp('<') || this.esOp('>') || this.esOp('<=') || this.esOp('>=') ||
      this.esOp('=') || this.esOp('==') || this.esOp('<>')
    ) {
      const op = this.actual.valor === '==' ? '=' : this.actual.valor;
      const linea = this.avanzar().linea;
      izq = { tipo: 'binaria', op, izq, der: this.parsearAditivo(), linea };
    }
    return izq;
  }

  /** @returns {Nodo} */
  parsearAditivo() {
    /** @type {Nodo} */
    let izq = this.parsearMultiplicativo();
    while (this.esOp('+') || this.esOp('-')) {
      const op = this.actual.valor;
      const linea = this.avanzar().linea;
      izq = { tipo: 'binaria', op, izq, der: this.parsearMultiplicativo(), linea };
    }
    return izq;
  }

  /** @returns {Nodo} */
  parsearMultiplicativo() {
    /** @type {Nodo} */
    let izq = this.parsearPotencia();
    while (this.esOp('*') || this.esOp('/') || this.esOp('%') || this.esPalabra('mod')) {
      const op = this.esPalabra('mod') ? '%' : this.actual.valor;
      const linea = this.avanzar().linea;
      izq = { tipo: 'binaria', op, izq, der: this.parsearPotencia(), linea };
    }
    // `a DIV b` es la división entera de otros pseudocódigos. Aquí solo puede
    // llegar en posición de operador (`div` como variable se lee arriba).
    if (this.esPalabra('div')) {
      throw new ErrorPseint(
        `"${this.actual.valor}" no existe en este pseudocódigo; para la división entera usa trunc(a / b).`,
        this.actual.linea,
      );
    }
    return izq;
  }

  /** @returns {Nodo} */
  parsearPotencia() {
    /** @type {Nodo} */
    const base = this.parsearUnario();
    if (this.esOp('^')) {
      const linea = this.avanzar().linea;
      return { tipo: 'binaria', op: '^', izq: base, der: this.parsearPotencia(), linea };
    }
    return base;
  }

  /** @returns {Nodo} */
  parsearUnario() {
    if (this.esOp('-')) {
      const linea = this.avanzar().linea;
      return { tipo: 'unaria', op: '-', valor: this.parsearUnario(), linea };
    }
    if (this.esOp('+')) {
      this.avanzar();
      return this.parsearUnario();
    }
    return this.parsearPrimario();
  }

  /** @returns {Nodo} */
  parsearPrimario() {
    const t = this.actual;
    if (t.tipo === 'NUMERO') {
      this.avanzar();
      return { tipo: 'numero', valor: t.valor, linea: t.linea };
    }
    if (t.tipo === 'CADENA') {
      this.avanzar();
      return { tipo: 'cadena', valor: t.valor, linea: t.linea };
    }
    if (this.esOp('(')) {
      this.avanzar();
      const e = this.parsearExpresion();
      this.esperarOp(')');
      return e;
    }
    if (t.tipo === 'ID') {
      const k = clave(t.valor);
      if (k === 'verdadero' || k === 'falso') {
        this.avanzar();
        return { tipo: 'logico', valor: k === 'verdadero', linea: t.linea };
      }
      // variable, índice de arreglo o llamada a función
      return this.parsearLValue();
    }
    throw new ErrorPseint('Expresión no válida.', t.linea);
  }
}

// ---------------------------------------------------------------------------
// Intérprete (recorrido del AST).
// ---------------------------------------------------------------------------

class Interprete {
  /**
   * @param {{ principal: any[], subprogramas: Map<string, any> }} programa
   * @param {string[]} entradas líneas de entrada para `Leer`
   */
  constructor(programa, entradas) {
    this.programa = programa;
    this.entradas = entradas;
    this.indiceEntrada = 0;
    /** @type {string[]} */
    this.salida = [];
    this.bufferLinea = '';
    this.pasos = 0;
    this.faltoEntrada = false;
    // Posición de la salida en el momento del ÚLTIMO `Leer`. Lo escrito antes
    // de pedir un dato es un mensaje de petición (prompt), no un resultado;
    // guardarla permite separarlos con exactitud, sin heurísticas.
    /** @type {{ linea: number, columna: number } | null} */
    this.marcaUltimaEntrada = null;
    // Tipo declarado con `Definir` (clave: "cadena", "entero"...) de cada
    // variable, por ámbito. Solo lo usa `Leer` (DEC-132): el resto del
    // intérprete sigue siendo dinámico.
    /** @type {WeakMap<Ambito, Map<string, string>>} */
    this.tiposDeclarados = new WeakMap();
  }

  /**
   * Salida producida DESPUÉS del último `Leer`. Si el algoritmo no lee nada,
   * es la salida entera. `Escribir Sin Saltar` puede dejar el prompt pegado
   * delante del resultado en la MISMA línea, por eso se recorta por columna.
   * @returns {string[]}
   */
  salidaTrasUltimaEntrada() {
    const marca = this.marcaUltimaEntrada;
    if (marca === null) return this.salida.slice();
    const resto = this.salida.slice(marca.linea);
    if (resto.length === 0) return [];
    return [resto[0].slice(marca.columna), ...resto.slice(1)];
  }

  /** Añade texto a la salida respetando `Sin Saltar` (acumula hasta el salto).
   * @param {string} texto @param {boolean} salto */
  emitir(texto, salto) {
    this.bufferLinea += texto;
    if (salto) {
      this.salida.push(this.bufferLinea);
      this.bufferLinea = '';
    }
  }

  finalizarSalida() {
    if (this.bufferLinea !== '') {
      this.salida.push(this.bufferLinea);
      this.bufferLinea = '';
    }
  }

  ejecutar() {
    const ambito = new Map();
    this.ejecutarBloque(this.programa.principal, ambito);
    this.finalizarSalida();
  }

  /** @param {any[]} sentencias @param {Map<string, any>} ambito */
  ejecutarBloque(sentencias, ambito) {
    for (const s of sentencias) this.ejecutarSentencia(s, ambito);
  }

  /** @param {number} linea */
  contarPaso(linea) {
    this.pasos += 1;
    if (this.pasos > LIMITE_PASOS) {
      throw new ErrorPseint('El programa hizo demasiadas operaciones (¿un bucle infinito?).', linea);
    }
  }

  /** @param {Nodo} s @param {Ambito} ambito */
  ejecutarSentencia(s, ambito) {
    this.contarPaso(s.linea);
    switch (s.tipo) {
      case 'definir': {
        // `Definir` NO da valor (DEC-133): usar la variable antes de asignarla
        // da «se usa antes de tener un valor», como dice la guía y como hacen
        // los diagramas. Hasta el 2026-09-13 la ponía a 0, y «olvidar poner el
        // contador a cero» --error frecuente de varios ejercicios-- aprobaba.
        let tipos = this.tiposDeclarados.get(ambito);
        if (!tipos) {
          tipos = new Map();
          this.tiposDeclarados.set(ambito, tipos);
        }
        for (const n of s.nombres) tipos.set(clave(n), clave(s.tipoDeclarado ?? ''));
        return;
      }
      case 'dimension':
        for (const arr of s.arreglos) {
          const tam = arr.dimensiones.map((/** @type {Nodo} */ d) => Math.trunc(this.evaluar(d, ambito)));
          ambito.set(clave(arr.nombre), this.crearArreglo(tam));
        }
        return;
      case 'asignacion':
        this.asignar(s.destino, this.evaluar(s.valor, ambito), ambito);
        return;
      case 'escribir': {
        const texto = s.partes.map((/** @type {Nodo} */ p) => this.aTexto(this.evaluar(p, ambito))).join('');
        this.emitir(texto, !s.sinSalto);
        return;
      }
      case 'leer':
        for (const destino of s.destinos) {
          const tipo =
            destino.tipo === 'variable'
              ? this.tiposDeclarados.get(ambito)?.get(clave(destino.nombre))
              : undefined;
          this.asignar(destino, this.leerEntrada(s.linea, tipo), ambito);
        }
        return;
      case 'si':
        if (this.esVerdadero(this.evaluar(s.condicion, ambito), s.linea)) this.ejecutarBloque(s.entonces, ambito);
        else this.ejecutarBloque(s.sino, ambito);
        return;
      case 'mientras':
        while (this.esVerdadero(this.evaluar(s.condicion, ambito), s.linea)) {
          this.contarPaso(s.linea);
          this.ejecutarBloque(s.cuerpo, ambito);
        }
        return;
      case 'repetir':
        do {
          this.contarPaso(s.linea);
          this.ejecutarBloque(s.cuerpo, ambito);
        } while (!this.esVerdadero(this.evaluar(s.condicion, ambito), s.linea));
        return;
      case 'para':
        this.ejecutarPara(s, ambito);
        return;
      case 'segun':
        this.ejecutarSegun(s, ambito);
        return;
      case 'llamadaSentencia':
        this.evaluar(s.llamada, ambito);
        return;
      default:
        throw new ErrorPseint(`Instrucción no soportada: ${s.tipo}.`, s.linea);
    }
  }

  /** @param {Nodo} s @param {Ambito} ambito */
  ejecutarPara(s, ambito) {
    let i = this.evaluar(s.desde, ambito);
    const fin = this.evaluar(s.hasta, ambito);
    const paso = s.paso === null ? 1 : this.evaluar(s.paso, ambito);
    if (paso === 0) throw new ErrorPseint('El paso del "Para" no puede ser 0.', s.linea);
    ambito.set(clave(s.variable), i);
    while ((paso > 0 && i <= fin) || (paso < 0 && i >= fin)) {
      this.contarPaso(s.linea);
      ambito.set(clave(s.variable), i);
      this.ejecutarBloque(s.cuerpo, ambito);
      i = this.numero(ambito.get(clave(s.variable)), s.linea) + paso;
    }
  }

  /** @param {Nodo} s @param {Ambito} ambito */
  ejecutarSegun(s, ambito) {
    const valor = this.evaluar(s.expr, ambito);
    for (const caso of s.casos) {
      for (const v of caso.valores) {
        if (this.igual(this.evaluar(v, ambito), valor)) {
          this.ejecutarBloque(caso.cuerpo, ambito);
          return;
        }
      }
    }
    if (s.deOtroModo) this.ejecutarBloque(s.deOtroModo, ambito);
  }

  /** @param {number[]} dimensiones @returns {any} */
  crearArreglo(dimensiones) {
    const [d, ...resto] = dimensiones;
    /** @type {{ esArreglo: boolean, datos: any[] }} */
    const arr = { esArreglo: true, datos: [] };
    for (let k = 0; k <= d; k += 1) {
      arr.datos[k] = resto.length === 0 ? 0 : this.crearArreglo(resto);
    }
    return arr;
  }

  /** Asigna `valor` a un destino (variable o elemento de arreglo).
   * @param {Nodo} destino @param {any} valor @param {Ambito} ambito */
  asignar(destino, valor, ambito) {
    if (destino.tipo === 'variable') {
      ambito.set(clave(destino.nombre), valor);
      return;
    }
    if (destino.tipo === 'indice') {
      const arr = ambito.get(clave(destino.nombre));
      if (!arr || !arr.esArreglo) throw new ErrorPseint(`"${destino.nombre}" no es un arreglo dimensionado.`, destino.linea);
      const indices = destino.indices.map((/** @type {Nodo} */ e) => Math.trunc(this.numero(this.evaluar(e, ambito), destino.linea)));
      let obj = arr;
      for (let k = 0; k < indices.length - 1; k += 1) {
        obj = obj.datos[indices[k]];
        if (!obj || !obj.esArreglo) throw new ErrorPseint('Índice de arreglo fuera de rango.', destino.linea);
      }
      const ultimo = indices[indices.length - 1];
      if (ultimo < 0 || ultimo >= obj.datos.length) throw new ErrorPseint('Índice de arreglo fuera de rango.', destino.linea);
      obj.datos[ultimo] = valor;
      return;
    }
    throw new ErrorPseint('Destino de asignación no válido.', destino.linea);
  }

  /**
   * @param {number} linea
   * @param {string} [tipoDeclarado] Clave del tipo del `Definir` de la variable.
   * @returns {any}
   */
  leerEntrada(linea, tipoDeclarado) {
    if (this.indiceEntrada >= this.entradas.length) {
      this.faltoEntrada = true;
      throw new ErrorPseint('El programa pidió más datos de los que hay en "Entradas".', linea);
    }
    const bruto = this.entradas[this.indiceEntrada];
    this.indiceEntrada += 1;
    this.marcaUltimaEntrada = { linea: this.salida.length, columna: this.bufferLinea.length };
    // Una variable declarada como texto recibe el texto tal cual (DEC-132):
    // «08001» sigue siendo «08001» y «falso» sigue siendo la palabra. Mismo
    // criterio que los diagramas (DEC-128).
    if (tipoDeclarado !== undefined && TIPOS_TEXTO.has(tipoDeclarado)) return bruto;
    // Sin tipo de texto, interpreta números automáticamente; si no, deja el texto.
    const t = bruto.trim();
    if (t !== '' && !Number.isNaN(Number(t))) return Number(t);
    if (clave(t) === 'verdadero') return true;
    if (clave(t) === 'falso') return false;
    return bruto;
  }

  // ---- Evaluación de expresiones ----
  /** @param {Nodo} e @param {Ambito} ambito @returns {any} */
  evaluar(e, ambito) {
    switch (e.tipo) {
      case 'numero':
        return e.valor;
      case 'cadena':
        return e.valor;
      case 'logico':
        return e.valor;
      case 'variable': {
        const k = clave(e.nombre);
        if (!ambito.has(k)) throw new ErrorPseint(`La variable "${e.nombre}" se usa antes de tener un valor.`, e.linea);
        return ambito.get(k);
      }
      case 'indice': {
        const arr = ambito.get(clave(e.nombre));
        if (!arr || !arr.esArreglo) throw new ErrorPseint(`"${e.nombre}" no es un arreglo dimensionado.`, e.linea);
        const indices = e.indices.map((/** @type {Nodo} */ ie) => Math.trunc(this.numero(this.evaluar(ie, ambito), e.linea)));
        let obj = arr;
        for (const idx of indices) {
          if (!obj || !obj.esArreglo || idx < 0 || idx >= obj.datos.length) {
            throw new ErrorPseint('Índice de arreglo fuera de rango.', e.linea);
          }
          obj = obj.datos[idx];
        }
        return obj;
      }
      case 'unaria':
        return this.evalUnaria(e, ambito);
      case 'binaria':
        return this.evalBinaria(e, ambito);
      case 'llamada':
        return this.evalLlamada(e, ambito);
      default:
        throw new ErrorPseint('Expresión no soportada.', e.linea);
    }
  }

  /** @param {Nodo} e @param {Ambito} ambito @returns {any} */
  evalUnaria(e, ambito) {
    if (e.op === '-') return -this.numero(this.evaluar(e.valor, ambito), e.linea);
    if (e.op === 'no') return !this.esVerdadero(this.evaluar(e.valor, ambito), e.linea);
    throw new ErrorPseint('Operador unario no válido.', e.linea);
  }

  /** @param {Nodo} e @param {Ambito} ambito @returns {any} */
  evalBinaria(e, ambito) {
    const op = e.op;
    if (op === 'y') return this.esVerdadero(this.evaluar(e.izq, ambito), e.linea) && this.esVerdadero(this.evaluar(e.der, ambito), e.linea);
    if (op === 'o') return this.esVerdadero(this.evaluar(e.izq, ambito), e.linea) || this.esVerdadero(this.evaluar(e.der, ambito), e.linea);
    const a = this.evaluar(e.izq, ambito);
    const b = this.evaluar(e.der, ambito);
    switch (op) {
      case '+':
        // Suma numérica; si algún operando es texto, concatena (comodidad).
        if (typeof a === 'string' || typeof b === 'string') return this.aTexto(a) + this.aTexto(b);
        return this.numero(a, e.linea) + this.numero(b, e.linea);
      case '-':
        return this.numero(a, e.linea) - this.numero(b, e.linea);
      case '*':
        return this.numero(a, e.linea) * this.numero(b, e.linea);
      case '/': {
        const d = this.numero(b, e.linea);
        if (d === 0) throw new ErrorPseint('División entre cero.', e.linea);
        return this.numero(a, e.linea) / d;
      }
      case '%': {
        const d = this.numero(b, e.linea);
        if (d === 0) throw new ErrorPseint('División entre cero (MOD).', e.linea);
        return this.numero(a, e.linea) % d;
      }
      case '^':
        return this.numero(a, e.linea) ** this.numero(b, e.linea);
      case '<':
        return this.compara(a, b, e.linea) < 0;
      case '>':
        return this.compara(a, b, e.linea) > 0;
      case '<=':
        return this.compara(a, b, e.linea) <= 0;
      case '>=':
        return this.compara(a, b, e.linea) >= 0;
      case '=':
        return this.igual(a, b);
      case '<>':
        return !this.igual(a, b);
      default:
        throw new ErrorPseint(`Operador "${op}" no válido.`, e.linea);
    }
  }

  /** @param {Nodo} e @param {Ambito} ambito @returns {any} */
  evalLlamada(e, ambito) {
    const nombre = clave(e.nombre);
    const args = e.args.map((/** @type {Nodo} */ a) => this.evaluar(a, ambito));
    // Función interna
    const interna = funcionInterna(nombre);
    if (interna) return interna.calcular(args, e.linea);
    // Subprograma definido por el usuario
    const sp = this.programa.subprogramas.get(nombre);
    if (sp) return this.llamarSubprograma(sp, e, ambito);
    throw new ErrorPseint(`Función o subproceso "${e.nombre}" no definido.`, e.linea);
  }

  /** @param {Nodo} sp @param {Nodo} e @param {Ambito} ambitoLlamador @returns {any} */
  llamarSubprograma(sp, e, ambitoLlamador) {
    if (e.args.length !== sp.params.length) {
      throw new ErrorPseint(`"${sp.nombre}" espera ${sp.params.length} parámetro(s), recibió ${e.args.length}.`, e.linea);
    }
    /** @type {Ambito} */
    const local = new Map();
    sp.params.forEach((/** @type {any} */ p, /** @type {number} */ i) => {
      local.set(clave(p.nombre), this.evaluar(e.args[i], ambitoLlamador));
    });
    this.ejecutarBloque(sp.cuerpo, local);
    // Escritura de vuelta de parámetros por referencia (a variables simples).
    sp.params.forEach((/** @type {any} */ p, /** @type {number} */ i) => {
      if (p.porReferencia && e.args[i].tipo === 'variable') {
        ambitoLlamador.set(clave(e.args[i].nombre), local.get(clave(p.nombre)));
      }
    });
    if (sp.retorno) {
      const k = clave(sp.retorno);
      return local.has(k) ? local.get(k) : 0;
    }
    return 0;
  }

  // ---- Utilidades de tipos ----
  /** @param {any} v @param {number} linea @returns {number} */
  numero(v, linea) {
    if (typeof v === 'number') return v;
    if (typeof v === 'boolean') return v ? 1 : 0;
    const n = Number(v);
    if (Number.isNaN(n)) throw new ErrorPseint(`Se esperaba un número y se obtuvo "${v}".`, linea);
    return n;
  }

  /** @param {any} v @param {number} linea @returns {boolean} */
  esVerdadero(v, linea) {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    throw new ErrorPseint('Se esperaba una condición Verdadero/Falso.', linea);
  }

  /** @param {any} a @param {any} b @returns {boolean} */
  igual(a, b) {
    if (typeof a === 'number' && typeof b === 'number') return a === b;
    if (typeof a === 'boolean' || typeof b === 'boolean') return Boolean(a) === Boolean(b);
    return this.aTexto(a) === this.aTexto(b);
  }

  /** @param {any} a @param {any} b @param {number} linea @returns {number} */
  compara(a, b, linea) {
    if (typeof a === 'string' || typeof b === 'string') {
      const sa = this.aTexto(a);
      const sb = this.aTexto(b);
      return sa < sb ? -1 : sa > sb ? 1 : 0;
    }
    const na = this.numero(a, linea);
    const nb = this.numero(b, linea);
    return na < nb ? -1 : na > nb ? 1 : 0;
  }

  /** @param {any} v @returns {string} */
  aTexto(v) {
    if (typeof v === 'boolean') return v ? 'VERDADERO' : 'FALSO';
    if (typeof v === 'number') {
      // Enteros sin decimales; reales con su representación natural.
      return Number.isInteger(v) ? String(v) : String(v);
    }
    if (v && v.esArreglo) return '<arreglo>';
    return String(v);
  }
}

// ---------------------------------------------------------------------------
// Funciones internas (núcleo de PSeInt). Reciben (args, linea).
// ---------------------------------------------------------------------------
/** @param {any[]} a @param {number} i @param {number} linea */
function reqNum(a, i, linea) {
  const v = a[i];
  const n = typeof v === 'boolean' ? (v ? 1 : 0) : Number(v);
  if (Number.isNaN(n)) throw new ErrorPseint('Argumento numérico inválido.', linea);
  return n;
}
/** @param {any[]} a @param {number} i */
function reqTxt(a, i) {
  const v = a[i];
  if (typeof v === 'boolean') return v ? 'VERDADERO' : 'FALSO';
  return String(v);
}

/** @typedef {{ datos: number, calcular: (a: any[], linea: number) => any }} FuncionInterna */

/**
 * Una función interna y los datos que admite (`redon(x)` admite 1).
 * @param {number} datos @param {FuncionInterna['calcular']} calcular
 * @returns {FuncionInterna}
 */
const interna = (datos, calcular) => ({ datos, calcular });

/** @type {Record<string, FuncionInterna>} */
const FUNCIONES_INTERNAS = {
  // Matemáticas
  raiz: interna(1, (a, l) => Math.sqrt(reqNum(a, 0, l))),
  rc: interna(1, (a, l) => Math.sqrt(reqNum(a, 0, l))),
  abs: interna(1, (a, l) => Math.abs(reqNum(a, 0, l))),
  trunc: interna(1, (a, l) => Math.trunc(reqNum(a, 0, l))),
  truncar: interna(1, (a, l) => Math.trunc(reqNum(a, 0, l))),
  redon: interna(1, (a, l) => Math.round(reqNum(a, 0, l))),
  redondear: interna(1, (a, l) => Math.round(reqNum(a, 0, l))),
  sen: interna(1, (a, l) => Math.sin(reqNum(a, 0, l))),
  cos: interna(1, (a, l) => Math.cos(reqNum(a, 0, l))),
  tan: interna(1, (a, l) => Math.tan(reqNum(a, 0, l))),
  exp: interna(1, (a, l) => Math.exp(reqNum(a, 0, l))),
  ln: interna(1, (a, l) => Math.log(reqNum(a, 0, l))),
  pi: interna(0, () => Math.PI),
  aleatorio: interna(2, (a, l) => {
    const lo = Math.trunc(reqNum(a, 0, l));
    const hi = Math.trunc(reqNum(a, 1, l));
    return lo + Math.floor(Math.random() * (hi - lo + 1));
  }),
  // Texto
  longitud: interna(1, (a) => reqTxt(a, 0).length),
  mayusculas: interna(1, (a) => reqTxt(a, 0).toUpperCase()),
  minusculas: interna(1, (a) => reqTxt(a, 0).toLowerCase()),
  subcadena: interna(3, (a, l) => {
    // PSeInt: Subcadena(cadena, desde, hasta) con índices base 0 inclusivos.
    const s = reqTxt(a, 0);
    const desde = Math.trunc(reqNum(a, 1, l));
    const hasta = Math.trunc(reqNum(a, 2, l));
    return s.substring(desde, hasta + 1);
  }),
  concatenar: interna(2, (a) => reqTxt(a, 0) + reqTxt(a, 1)),
  convertiratexto: interna(1, (a) => reqTxt(a, 0)),
  convertiranumero: interna(1, (a, l) => {
    const n = Number(String(a[0]).trim());
    if (Number.isNaN(n)) throw new ErrorPseint('ConvertirANumero: el texto no es un número.', l);
    return n;
  }),
  esnumero: interna(
    1,
    (a) => !Number.isNaN(Number(String(a[0]).trim())) && String(a[0]).trim() !== '',
  ),
  mayuscula: interna(1, (a) => reqTxt(a, 0).toUpperCase()),
};

/**
 * La función interna que se llama `nombre`, si existe. Solo mira las propias
 * de la tabla: `constructor` o `toString` no son funciones de PSeInt.
 * @param {string} nombre @returns {FuncionInterna | undefined}
 */
function funcionInterna(nombre) {
  const k = clave(nombre);
  return Object.prototype.hasOwnProperty.call(FUNCIONES_INTERNAS, k)
    ? FUNCIONES_INTERNAS[k]
    : undefined;
}

// ---------------------------------------------------------------------------
// API pública
// ---------------------------------------------------------------------------
/**
 * Analiza pseudocódigo y devuelve su árbol sintáctico, SIN ejecutarlo.
 *
 * Se expone (DEC-107) para que el traductor a IR reutilice esta gramática en
 * vez de escribir una segunda. Dos analizadores del mismo lenguaje divergen
 * siempre, y el día que lo hicieran, "ejecutar" y "traducir" aceptarían
 * programas distintos -- que es la peor forma de que un alumno descubra que
 * su código está mal.
 *
 * Lanza `ErrorPseint` (con `linea`) si el texto no es pseudocódigo válido.
 * @param {string} codigo
 * @returns {{ principal: any[], subprogramas: Map<string, any> }}
 */
export function analizarPseudocodigo(codigo) {
  return new Parser(tokenizar(String(codigo ?? ''))).parsearPrograma();
}

/**
 * Ejecuta un algoritmo de pseudocódigo PSeInt y devuelve su salida.
 * @param {string} codigo Texto completo (Algoritmo/Proceso + subprocesos).
 * @param {{ entradas?: string[] }} [opciones] Líneas de entrada para `Leer`.
 * @returns {ResultadoEjecucion}
 */
export function ejecutarPseudocodigo(codigo, opciones = {}) {
  const entradas = Array.isArray(opciones.entradas) ? opciones.entradas.map((e) => String(e)) : [];
  /** @type {Interprete | null} */
  let interprete = null;
  try {
    const tokens = tokenizar(String(codigo ?? ''));
    const programa = new Parser(tokens).parsearPrograma();
    interprete = new Interprete(programa, entradas);
    interprete.ejecutar();
    return {
      salida: interprete.salida,
      salidaSinPrompts: interprete.salidaTrasUltimaEntrada(),
      error: null,
      entradasConsumidas: interprete.indiceEntrada,
      faltoEntrada: false,
    };
  } catch (e) {
    if (e instanceof ErrorPseint) {
      // Se conserva la salida PARCIAL producida hasta el error (todo lo que
      // `Escribir` imprimió antes). Es imprescindible para el input interactivo
      // por modal (`run-console.js`): cuando `faltoEntrada`, el modal muestra
      // esa salida parcial como contexto antes de pedir el dato.
      return {
        salida: interprete ? interprete.salida : [],
        salidaSinPrompts: interprete ? interprete.salidaTrasUltimaEntrada() : [],
        error: { mensaje: e.message, linea: e.linea },
        entradasConsumidas: interprete ? interprete.indiceEntrada : 0,
        faltoEntrada: /más datos/.test(e.message),
      };
    }
    throw e;
  }
}
